// DebugPanel entry (IMPLEMENTATION §3 M12): the small part in the initial bundle. The panel itself
// (DebugPanel.tsx) is a lazy chunk (`import()`, lead decision L7) loaded on `?debug` or after
// `versionTapsForPanel` (7) taps on the version label — `openDebugOnTaps(el, …)`; M7 attaches it to the
// Settings version label, M12 to the spike status chip. `tools/check-size.ts` fails if the panel ever lands in
// the initial JS.
import type { UiData } from '../data/schema.ts';
import type { GameController } from '../game/GameController.ts';
import type { Cell, Layout } from '../layout/computeLayout.ts';
import type { Telemetry } from './telemetry.ts';
import type { TraceRecorder } from './trace.ts';

/** What the lazy panel can reach (built by main.ts; getters because the game can be replaced). */
export interface DebugContext {
  win: Window & typeof globalThis;
  game(): GameController;
  layout(): Layout | null;
  /** The last tapped board cell (the panel's default target cell). */
  lastTap(): Cell | null;
  telemetry: Telemetry;
  trace: TraceRecorder;
  /** The save envelope (JSON) of the running game — built in memory, never written to the real save. */
  saveText(): string;
  /** The cold-open seed of this game (null: started from a save / scenario). */
  seed(): number | null;
  /** DEV server: "Send to PC" (POST /__gm/upload) is offered. */
  dev: boolean;
  version: string;
  buildId: string;
  /** ui.json debug. */
  cfg: UiData['debug'];
  /** UI refresh rate of the panel (ui.json render.snapshotHz). */
  refreshHz: number;
}

export interface TapCounter {
  /** Counts a tap at `ms`; true on the tap that completes the run (the counter then starts over). */
  tap(ms: number): boolean;
  reset(): void;
}

/** `taps` taps in a row, each at most `gapMs` after the previous one (Android "tap build number 7 times"). */
export function createTapCounter(taps: number, gapMs: number): TapCounter {
  let n = 0;
  let last = Number.NEGATIVE_INFINITY;
  return {
    tap(ms) {
      n = ms - last <= gapMs ? n + 1 : 1;
      last = ms;
      if (n >= taps) {
        n = 0;
        last = Number.NEGATIVE_INFINITY;
        return true;
      }
      return false;
    },
    reset() {
      n = 0;
      last = Number.NEGATIVE_INFINITY;
    },
  };
}

export interface TapTarget {
  /** The label (or a getter: UI frameworks may re-create it). Hit-tested by its rect, so a label with
   *  `pointer-events: none` (the #ui chrome) works too. */
  el: HTMLElement | (() => HTMLElement | null);
  taps: number;
  gapMs: number;
}

/**
 * Calls `onOpen` after `taps` quick taps on the element (pointerdown inside its client rect, capture phase on
 * the window: the tap still reaches whatever is underneath). Returns an uninstaller.
 */
export function openDebugOnTaps(win: Window, t: TapTarget, onOpen: () => void): () => void {
  const counter = createTapCounter(t.taps, t.gapMs);
  const onDown = (e: PointerEvent): void => {
    const el = typeof t.el === 'function' ? t.el() : t.el;
    if (!el?.isConnected) return;
    const r = el.getBoundingClientRect();
    const inside =
      r.width > 0 &&
      r.height > 0 &&
      e.clientX >= r.left &&
      e.clientX <= r.right &&
      e.clientY >= r.top &&
      e.clientY <= r.bottom;
    if (!inside) {
      counter.reset();
      return;
    }
    if (counter.tap(e.timeStamp)) onOpen();
  };
  win.addEventListener('pointerdown', onDown, true);
  return () => win.removeEventListener('pointerdown', onDown, true);
}

export interface DebugPanelHandle {
  /** Opens (expands) the panel, loading its chunk on first use. */
  open(): Promise<void>;
  /** True once the panel chunk is loaded and mounted. */
  loaded(): boolean;
}

/** The lazy loader: the chunk is fetched on the first `open()` only. */
export function createDebugPanel(ctx: DebugContext): DebugPanelHandle {
  let mounted: { expand(): void } | null = null;
  let loading: Promise<void> | null = null;
  return {
    loaded: () => mounted !== null,
    open() {
      if (mounted) {
        mounted.expand();
        return Promise.resolve();
      }
      if (!loading) {
        loading = import('./DebugPanel.tsx').then((m) => {
          mounted = m.mountDebugPanel(ctx);
        });
        loading.catch(() => {
          loading = null; // a failed chunk load (offline) may be retried
        });
      }
      return loading;
    },
  };
}
